import renderInputField from 'components/FormControls/textInput'
import { Button, Col, Flag, Grid, Icon, Typography } from 'gu-ui-kit'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { useTranslation } from 'react-i18next'
import { Field, Form, formValueSelector, reduxForm } from 'redux-form'
import validator from 'utils/asyncValidator'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import { useSelector } from 'react-redux'
import { ROUTES } from 'constants/routes'
import useRecaptchaOutsideClick from 'utils/hooks/useRecaptchaOutsideClick'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import phoneFieldNormalize from 'utils/normalize/phoneFieldNormalize'
import renderMaskedInput from 'components/FormControls/maskedInputField'
import renderSelect from 'components/FormControls/select'
import renderCheckbox from 'components/FormControls/checkboxField'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { useCountries } from 'modules/Countries/hooks/useCountries'
import { REGISTRATION_SUBMIT } from 'modules/Auth/types'
import { validationSchema } from 'modules/Forms/Auth/Registration/validationSchema'
import styles from 'modules/Auth/Registration/Registration.module.scss'
import { onlyNumbers } from 'utils/normalize/onlyNumbers'
import omit from 'lodash/omit'
import { IS_APP_PRODUCT_GETUNIQ } from 'utils/constants/product'

let RegistrationForm = ({ handleSubmit, submitting, form, onSuccess, autofill }) => {
  const { t } = useTranslation()
  const phoneRef = useRef(null)
  const captchaRef = useRef(null)
  const [captchaKey, setCaptchaKey] = useState(null)
  const { app: { i18n } = {}, countries: { data: countries } = {} } = useSelector(
    ({ app, countries }) => ({
      app,
      countries,
    }),
  )
  const breakpoint = useBreakpoint()
  const pathGen = useGeneratePathI18n()
  const { getData, resetData } = useCountries()

  const selector = formValueSelector(form)
  const { phone_country_code, apply_agreement, phone_mask } = useSelector((state) => ({
    ...selector(state, 'phone_country_code', 'apply_agreement', 'phone_mask'),
  }))

  useEffect(() => {
    autofill('locale', i18n)
  }, [i18n])

  useEffect(() => {
    setCaptchaKey(
      window.GOOGLE_CAPTCHA_SITE_KEY && !window.GOOGLE_CAPTCHA_SITE_KEY.match(/({{)|(}})/)
        ? window.GOOGLE_CAPTCHA_SITE_KEY
        : '6LdD4ncUAAAAANcnFRZ_epMrlwp7NktSu4rhcSpq',
    )
  }, [])

  useRecaptchaOutsideClick(form)

  const submit = async (values, dispatch) => {
    if (!captchaRef?.current) {
      return
    }

    captchaRef.current.reset()
    captchaRef.current.execute()

    const token = await captchaRef.current.executeAsync()

    if (!token) {
      return
    }

    const csrf = await fetch('/api/app/csrf/api')
      .then((response) => response.json())
      .then((resp) => resp.data.token)

    return new Promise((resolve, reject) => {
      dispatch({
        meta: { resolve, reject },
        type: REGISTRATION_SUBMIT,
        payload: {
          data: {
            ...omit(values, ['phone_code', 'apply_agreement', 'phone_mask']),
            g_recaptcha_response: token,
            token: csrf,
          },
          formName: form,
        },
      })
    }).then(() => {
      typeof window?.ym === 'function' && window.ym(32793525, 'reachGoal', 'register_new')
      onSuccess(values.email)
    })
  }

  useEffect(() => {
    getData({ has_phone_codes: true })

    return () => resetData()
  }, [])

  const countriesOptions = useMemo(
    () =>
      countries.reduce(
        (acc, { code, phone = [] }) => [
          ...acc,
          ...phone.map(({ code: phoneCode, mask: phoneMask = null }) => ({
            code,
            phoneCode,
            phoneMask,
            value: code,
            label: t(`countries::${code}`),
          })),
        ],
        [],
      ),
    [countries],
  )

  const handleChange = useCallback(({ phoneCode, phoneMask }) => {
    const [countryCode, districtCode] = phoneCode.split(' ')

    autofill('phone', districtCode ? `${countryCode} (${districtCode})` : countryCode)
    autofill('phone_code', countryCode)

    autofill('phone_mask', phoneMask.replace(/[-]/g, ' '))

    document.querySelector('[name=phone]').focus()
  }, [])

  const handlePhoneFocus = useCallback((e) => {
    setTimeout(function () {
      const value = e.target.value.split(' ')
      e.target.setSelectionRange(value[0].length + 1, value[0].length + 1)
    }, 100)
  }, [])

  const handlePhoneChange = useCallback(
    (e) => {
      const value = onlyNumbers(e.target.value)

      let countryCode = value.substring(0, 3)

      if (countryCode === '8') {
        countryCode = '7'
        setTimeout(() => {
          autofill('phone', countryCode)
        }, 100)
      }

      const { phoneCode = null, code = null } =
        countriesOptions?.find(({ phoneCode }) => phoneCode === phone_country_code) ?? {}

      if (phoneCode) {
        autofill('phone_code', phoneCode)
        autofill('phone_country_code', code)
      }
    },
    [countriesOptions],
  )

  return (
    <Form
      onSubmit={handleSubmit(submit)}
      noValidate
    >
      {captchaKey && (
        <ReCAPTCHA
          ref={captchaRef}
          size="invisible"
          sitekey={captchaKey}
          hl={i18n}
        />
      )}
      <div className={'margin-bottom-16'}>
        <Field
          fullWidth
          name="email"
          type="email"
          component={renderInputField}
          t={t}
          inputMode={'email'}
          label={t('auth::forms.registration.fields.email.label')}
        />
      </div>
      <div className={'margin-bottom-16'}>
        <Grid
          gap={16}
          wrap={false}
        >
          <Col flexGrow={0}>
            <Field
              onChangeOption={handleChange}
              name="phone_country_code"
              mode={'filter'}
              component={renderSelect}
              dropdownClassName={styles['registration__select-dropdown']}
              showEmptyOption={false}
              noResultsLabel={t('auth::forms.registration.fields.country_code.no_result')}
              filterPlaceholder={t(
                'auth::forms.registration.fields.country_code.filter_placeholder',
              )}
              fullWidth
              t={t}
              options={countriesOptions}
              ControlProps={{
                startAdornment: phone_country_code ? (
                  <Flag
                    className={'margin-left-16'}
                    name={phone_country_code}
                  />
                ) : (
                  <Icon
                    className={'margin-left-16'}
                    size={'m'}
                    name={'globe'}
                    color={'Label-Fifth'}
                  />
                ),
              }}
              activeOptionLabelTemplate={({ phoneCode }) => `+${phoneCode}`}
              optionLabelTemplate={({ phoneCode, code }) => (
                <Grid
                  justifyContent={'between'}
                  alignItems={'center'}
                  wrap={false}
                  gap={16}
                >
                  <Grid
                    wrap={false}
                    gap={8}
                    justifyContent={'start'}
                    alignItems={'center'}
                  >
                    <Flag name={code} />

                    <Typography
                      variant={'Body-15'}
                      color={'Label-Fifth'}
                    >
                      {t(`countries::${code}`)}
                    </Typography>
                  </Grid>

                  <Typography
                    noWrap
                    variant={'Body-15'}
                    color={'Label-Secondary'}
                  >
                    +{phoneCode}
                  </Typography>
                </Grid>
              )}
              noLabel
            />
          </Col>
          <Col flexGrow={1}>
            <Field
              ref={phoneRef}
              fullWidth
              normalize={phoneFieldNormalize}
              onChange={handlePhoneChange}
              name="phone"
              onFocus={handlePhoneFocus}
              component={renderMaskedInput}
              placeholderChar={'\u2002'}
              mask={() => phone_mask.split('').map((d) => (d === '9' ? /[0-9]/ : d))}
              inputMode={'tel'}
              label={t('auth::forms.registration.fields.phone.label')}
              t={t}
            />
          </Col>
        </Grid>
      </div>
      <div className={'margin-bottom-16'}>
        <Field
          fullWidth
          name="password"
          type="password"
          inputMode={'text'}
          component={renderInputField}
          autocomplete="new-password"
          label={t('auth::forms.login.fields.password.label')}
          t={t}
          clearButton
          revealButton
        />
      </div>
      <div className={breakpoint.fbs ? 'margin-bottom-32' : 'margin-bottom-40'}>
        <Field
          name="apply_agreement"
          checked={apply_agreement}
          component={renderCheckbox}
          noWrap={false}
          label={
            <Typography variant={'Body-15'}>
              <span
                dangerouslySetInnerHTML={{
                  __html: t('auth::forms.registration.fields.personal_data.label', {
                    url: pathGen(ROUTES.PRIVACY_POLICY),
                  }),
                }}
              />
            </Typography>
          }
        />
      </div>
      <Field
        fullWidth
        name="i18n"
        type="text"
        component={renderInputField}
        t={t}
        hidden={true}
      />

      <Grid
        direction={breakpoint.fbs ? 'column-reverse' : 'row'}
        alignItems={'center'}
        justifyContent={'start'}
        wrap={false}
        gap={16}
      >
        {IS_APP_PRODUCT_GETUNIQ ? (
          <Button
            fullWidth
            component={'a'}
            target={'_blank'}
            rel={'noopener noreferrer'}
            href={ROUTES.AUTH_ADMITAD_AUTH}
            startIcon={<Icon name={'admitad'} />}
            variant={'secondary'}
            grey
            size={'l'}
          >
            Admitad
          </Button>
        ) : null}
        <Button
          disabled={submitting}
          fullWidth
          size="l"
          variant="primary"
          type="submit"
        >
          {t('common::form_buttons.register')}
        </Button>
      </Grid>
    </Form>
  )
}

RegistrationForm = reduxForm({
  form: 'RegistrationForm',
  updateUnregisteredFields: true,
  enableReinitialize: true,
  initialValues: {
    email: '',
    phone_country_code: IS_APP_PRODUCT_GETUNIQ ? 'ru' : 'us',
    phone_mask: IS_APP_PRODUCT_GETUNIQ ? '+9 999 999 99 99' : '+9 999 999 9999',
    phone: IS_APP_PRODUCT_GETUNIQ ? 7 : 1,
    password: '',
  },
  shouldAsyncValidate: shouldAsyncValidate,
  asyncValidate: validator(validationSchema),
})(RegistrationForm)

RegistrationForm.propTypes = {
  onSuccess: PropTypes.func,
  handleSubmit: PropTypes.func,
  autofill: PropTypes.func,
  submitting: PropTypes.bool,
  form: PropTypes.string,
}

RegistrationForm.defaultValues = {
  onSuccess: () => {},
  handleSubmit: () => {},
  submitting: false,
  form: 'RegistrationForm',
}

export default RegistrationForm
